import { css } from '@emotion/css';
import {
  ColorSwatch,
  Divider,
  Group,
  getThemeColor,
  Progress,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useScrollShadow } from 'hooks/use-scroll-shadow';
import { i18nKeys, useTranslation } from 'locales';
import { AnalyticsCard } from 'modules/Analytics/components/AnalyticsCard';
import { entries, firstBy, sortBy, sum } from 'remeda';
import { useLoadGroupInvoiceTotals } from 'shared/hooks/use-load-group-invoice-totals';
import { useAgedBalanceColors } from 'shared/hooks/utils/use-aged-balance-colors';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';

import { useConsolidation } from './consolidation';
import { tooltipCardProps } from './tooltip-card';
import { useShareFormatter } from './use-share-formatter';

const AGEING_BUCKETS = ['notDue', '0', '30', '60', '90'] as const;

type GroupByCompanyProps = { selectedCompanies: ReadonlyArray<string> };

export const GroupByCompany = ({ selectedCompanies }: GroupByCompanyProps) => {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter();
  const shareFormatter = useShareFormatter();
  const colors = { ...useAgedBalanceColors(), notDue: 'purple.3' };
  const theme = useMantineTheme();
  const { groupInvoiceTotals, groupInvoiceTotalsError } = useLoadGroupInvoiceTotals({
    companies: selectedCompanies,
  });
  const { groupAnalytics } = useConsolidation(selectedCompanies);

  const latestDsoOf = (company: string) => {
    const months = groupAnalytics?.companyDsos.find(line => line.company === company)?.months;
    return months && firstBy(entries(months), [([month]) => month, 'desc'])?.[1];
  };

  const ageingOf = (company: string) => {
    const ageing = groupAnalytics?.contributions.find(
      contribution => contribution.company === company,
    )?.agedBalance;
    const total = ageing ? sum(AGEING_BUCKETS.map(bucket => ageing[bucket])) : 0;
    if (!ageing || total === 0) return null;
    return {
      total,
      buckets: AGEING_BUCKETS.map(bucket => ({
        key: bucket,
        amount: ageing[bucket],
        part: ageing[bucket] / total,
        color: colors[bucket],
        label: t(i18nKeys.ANALYTICS.AGED_BALANCE[bucket]),
      })),
    };
  };

  const rows = groupInvoiceTotals
    ? sortBy(groupInvoiceTotals.companies ?? [], [
        ({ totalInvoices }) => totalInvoices,
        'desc',
      ]).map(row => {
        const share = groupInvoiceTotals.totalInvoices
          ? row.totalInvoices / groupInvoiceTotals.totalInvoices
          : 0;
        const ageing = ageingOf(row.company);
        return {
          ...row,
          dso: latestDsoOf(row.company),
          share,
          ageing,
          segments: ageing
            ? ageing.buckets.map(bucket => ({ ...bucket, value: share * bucket.part }))
            : [{ key: row.company, value: share, color: 'purple.6', label: row.name }],
        };
      })
    : [];

  const { topShadow, bottomShadow, scrollableRef } = useScrollShadow(rows.length);

  return (
    <AnalyticsCard
      title={t(i18nKeys.GROUP.BY_COMPANY)}
      actions={
        <Text size="sm" c="dimmed">
          {t(i18nKeys.GROUP.BY_COMPANY_SUBTITLE)}
        </Text>
      }
      panelProps={{ fullHeight: true }}
    >
      <ScrollArea mx={-8} scrollbars="y" offsetScrollbars="present" viewportRef={scrollableRef}>
        {topShadow}
        {bottomShadow}
        <Stack gap="md" px={8}>
          {groupInvoiceTotals == null && !groupInvoiceTotalsError && (
            <Skeleton h={80} radius={8} aria-busy />
          )}
          {groupInvoiceTotalsError && <Text>{t(i18nKeys.GROUP.ANALYTICS_UNAVAILABLE)}</Text>}
          {rows.map(({ company, name, totalInvoices, dso, clients, share, ageing, segments }) => (
            <Tooltip
              key={company}
              {...tooltipCardProps}
              disabled={!ageing}
              label={
                ageing && (
                  <Stack gap={6}>
                    <Text size="sm" fw={600} c="gray.7">
                      {name}
                    </Text>
                    <Title order={3} fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatter.format(ageing.total)}
                    </Title>
                    <Divider color="gray.2" mt={8} mb={6} />
                    {ageing.buckets.map(({ key, label, amount, part, color }) => (
                      <Group key={key} justify="space-between" gap={12} wrap="nowrap">
                        <Group gap={7} wrap="nowrap">
                          <ColorSwatch color={getThemeColor(color, theme)} size={8} radius={2} />
                          <Text size="sm" c="gray.7">
                            {label}
                          </Text>
                        </Group>
                        <Group gap={12} wrap="nowrap">
                          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatter.format(amount)}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {shareFormatter.format(part)}
                          </Text>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                )
              }
            >
              <Stack gap={4} mx={-8} px={8} py={6} className={styles.row}>
                <Group justify="space-between">
                  <Text c="gray.10">{name}</Text>
                  <Text fw={600} c="gray.10">
                    {formatter.format(totalInvoices)}
                  </Text>
                </Group>
                <Progress.Root role="group" aria-label={name} size={8} radius="xl" bg="purple.1">
                  {segments.map(({ key, value, color, label }) => (
                    <Progress.Section
                      key={key}
                      value={value * 100}
                      color={color}
                      withAria={false}
                      aria-label={label}
                    />
                  ))}
                </Progress.Root>
                <Text size="xs" c="dimmed">
                  {t(i18nKeys.GROUP.SHARE_OF_GROUP, {
                    share: shareFormatter.format(share),
                  })}
                  {dso != null &&
                    ` · ${t(i18nKeys.GROUP.COMPANY_DSO, { days: t(i18nKeys.ANALYTICS.DAYS_SHORT, { count: dso }) })}`}
                  {clients != null && ` · ${t(i18nKeys.GROUP.CLIENT_COUNT, { count: clients })}`}
                </Text>
              </Stack>
            </Tooltip>
          ))}
        </Stack>
      </ScrollArea>
    </AnalyticsCard>
  );
};

const styles = {
  row: css`
    border-radius: 8px;
    transition: background-color 150ms ease;

    &:hover {
      background-color: var(--mantine-color-purple-0);
    }
  `,
};
